import * as LucideIcons from 'lucide-react';
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  Settings,
  Sidebar,
  Zap,
  // BellIcon,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Outlet,
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  getBreadcrumbs,
  NAVIGATION_ITEMS,
  ROUTES,
  STORAGE_KEYS,
} from '../../constants';
import { useUser } from '../../hooks/useUser';
import { useAuthInitialization } from '../../hooks/useAuthInitialization';
import {
  isAuthenticated,
  logout,
} from '../../services/authService/authService';
import { RootState } from '../../store/rootReducer';
import {
  selectCurrentUser,
  selectUserDataError,
  selectUserDataLoading,
  selectWorkspace,
  selectWorkspaceDataError,
  selectWorkspaceDataLoading,
} from '../../store/user/user.selector';
import { clearUserError } from '../../store/user/user.action';
import { useToast } from '../../contexts/ToastContext';
import WorkspaceDropdown from '../../components/common/WorkspaceDropdown';

const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  // Redux state
  const currentUser = useSelector((state: RootState) =>
    selectCurrentUser(state)
  );
  const userDataLoading = useSelector((state: RootState) =>
    selectUserDataLoading(state)
  );
  const workspaceDataLoading = useSelector((state: RootState) =>
    selectWorkspaceDataLoading(state)
  );
  const userDataError = useSelector((state: RootState) =>
    selectUserDataError(state)
  );
  const workspaceDataError = useSelector((state: RootState) =>
    selectWorkspaceDataError(state)
  );

  // Workspace state
  const currentWorkspace = useSelector((state: RootState) =>
    selectWorkspace(state)
  );

  // Local state
  const [userDropdownOpen, setUserDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  // Use the custom hook for user data utilities
  const { getUserDisplayName, getUserInitials, getUserEmail, getUserAvatar } =
    useUser();

  // Use the professional auth initialization hook
  const {
    isInitialized,
    isLoading,
    error: authError,
  } = useAuthInitialization();

  // Handle URL token parameter (for workspace switching or email links)
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken && isInitialized) {
      // Store the new token
      localStorage.setItem(STORAGE_KEYS.TOKEN, urlToken);

      // Remove token from URL for security
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      // Reload the page to reinitialize with new token
      window.location.reload();
    }
  }, [searchParams, isInitialized]);

  // Handle authentication check
  useEffect(() => {
    if (isInitialized && !isAuthenticated()) {
      navigate('/');
    }
  }, [isInitialized, navigate]);

  // Handle authentication errors
  useEffect(() => {
    if (userDataError?.includes('401') || workspaceDataError?.includes('401')) {
      logout();
      return;
    }
  }, [userDataError, workspaceDataError]);

  // Show toast notifications for errors
  useEffect(() => {
    if (authError) {
      showToast({
        type: 'error',
        title: 'Error',
        message: authError,
      });
    }
  }, [authError, showToast]);

  useEffect(() => {
    if (userDataError && !userDataError.includes('401')) {
      showToast({
        type: 'error',
        title: 'User Data Error',
        message: userDataError,
      });
      dispatch(clearUserError());
    }
  }, [userDataError, showToast, dispatch]);

  useEffect(() => {
    if (workspaceDataError && !workspaceDataError.includes('401')) {
      showToast({
        type: 'error',
        title: 'Workspace Error',
        message: workspaceDataError,
      });
      dispatch(clearUserError());
    }
  }, [workspaceDataError, showToast, dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Page loading on route changes (overlay only over content, sidebar visible)
  useEffect(() => {
    setPageLoading(true);
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleLogout = (): void => {
    logout();
  };

  const isActiveRoute = (route: string): boolean => {
    const regex = new RegExp(`(^|/)${route}(/|$)`);
    return regex.test(location.pathname);
  };

  const handleSettingsClick = (): void => {
    setUserDropdownOpen(false);
    navigate(ROUTES.SETTINGS);
  };

  const handleLogoutClick = (): void => {
    setUserDropdownOpen(false);
    handleLogout();
  };

  const breadcrumbs = getBreadcrumbs(location.pathname);

  // Show loading state
  const loading = isLoading || userDataLoading || workspaceDataLoading;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-card">
      {/* Sidebar */}
      <aside
        className={`bg-sidebar flex flex-col fixed left-0 top-0 h-screen z-30 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}
      >
        {/* Sidebar Header */}
        <div className="px-4 py-6 border-b border-sidebar-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-sidebar-brand rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-sidebar-primary-foreground" />
            </div>
            <div
              className={`flex-1 min-w-0 ${sidebarOpen ? 'block' : 'hidden'}`}
            >
              <WorkspaceDropdown
                currentWorkspace={currentWorkspace.workspace}
              />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 py-2 overflow-y-auto scrollbar-thin`}>
          {NAVIGATION_ITEMS.map((item) => {
            const IconComponent =
              (LucideIcons as any)[item.icon] || LucideIcons.Square;
            const isActive = isActiveRoute(item.route);
            return (
              <div key={item.id} className="px-2 py-0.5">
                <Link
                  className={`flex items-center rounded-md w-full text-sm font-medium transition-all duration-300
                      ${sidebarOpen ? 'gap-3 px-3 py-2' : 'gap-0 px-3 py-3 justify-center'}
                      ${
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/80 hover:bg-sidebar-muted/20 hover:text-sidebar-foreground'
                      }
                  `}
                  to={item.route}
                  title={item.label}
                >
                  <IconComponent className="w-4 h-4 flex-shrink-0" />
                  <span
                    className={`whitespace-nowrap transition-all duration-300
                      ${
                        sidebarOpen
                          ? 'opacity-100 translate-x-0 ml-0'
                          : 'opacity-0 -translate-x-2 w-0 overflow-hidden'
                      }`}
                  >
                    {item.label}
                  </span>
                </Link>
              </div>
            );
          })}
        </nav>

        {/* User Profile Footer */}
        <div className="border-t border-sidebar-border p-3 w-full overflow-x-hidden">
          <button
            onClick={() => setUserDropdownOpen(!userDropdownOpen)}
            className={`flex items-center gap-3 w-full rounded-lg hover:bg-sidebar-accent/50 ${sidebarOpen ? 'p-2' : 'py-2 px-1'}`}
          >
            {getUserAvatar() ? (
              <img
                src={getUserAvatar() || ''}
                alt={`${getUserDisplayName()}'s avatar`}
                className={`rounded-full object-cover ${sidebarOpen ? 'w-8 h-8' : 'w-8 h-8'}`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const nextSibling = target.nextElementSibling as HTMLElement;
                  if (nextSibling) {
                    nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div
              className={`w-8 h-8 bg-sidebar-primary rounded-full flex items-center justify-center text-sidebar-primary-foreground text-sm font-medium ${getUserAvatar() ? 'hidden' : 'flex'}`}
            >
              {getUserInitials()}
            </div>
            <div
              className={`flex-1 text-left ${sidebarOpen ? 'block' : 'hidden'}`}
            >
              <p className="text-sm font-medium text-sidebar-foreground">
                {getUserDisplayName()}
              </p>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-sidebar-muted transition-transform ${userDropdownOpen ? 'rotate-180' : ''} ${sidebarOpen ? 'block' : 'hidden'}`}
            />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 flex flex-col ${sidebarOpen ? 'ml-64' : 'ml-16'} transition-all duration-300`}
      >
        {/* Header */}
        <header className="sticky top-0 z-10 flex justify-between h-14 items-center gap-4 border-b px-4 lg:px-6 bg-background">
          <div className="flex items-center gap-4 min-w-0">
            <Sidebar
              className="w-4 h-4 cursor-pointer flex-shrink-0"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            />
            <div className="h-6 w-px bg-border flex-shrink-0" />
            <nav
              className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0"
              aria-label="Breadcrumb"
            >
              {breadcrumbs.map((item, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return (
                  <span
                    key={item.path}
                    className="flex items-center gap-1.5 min-w-0"
                  >
                    {index > 0 && (
                      <ChevronRight className="w-4 h-4 flex-shrink-0 text-muted-foreground/70" />
                    )}
                    {isLast ? (
                      <span className="font-semibold text-card-foreground truncate">
                        {item.label}
                      </span>
                    ) : (
                      <Link
                        to={item.path}
                        state={
                          item.path.includes('/campaigns/builder/')
                            ? { openWorkflow: true }
                            : undefined
                        }
                        className="truncate hover:text-card-foreground transition-colors"
                      >
                        {item.label}
                      </Link>
                    )}
                  </span>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications */}
            {/* <button className="p-2 rounded-lg hover:bg-muted-foreground/20 transition-colors">
              <BellIcon className="w-5 h-5 text-muted-foreground" />
            </button> */}
            {/* Settings */}
            <Link
              className="p-2 rounded-lg hover:bg-muted-foreground/20 transition-colors"
              to={ROUTES.SETTINGS}
            >
              <Settings className="w-5 h-5 text-muted-foreground" />
            </Link>
          </div>
        </header>

        {/* User Dropdown (positioned relative to sidebar) */}
        {userDropdownOpen && (
          <div
            ref={dropdownRef}
            className={`fixed bottom-2 ml-2 w-56 bg-popover rounded-lg shadow-lg border border-border z-50 ${sidebarOpen ? 'left-64' : 'left-16'}`}
          >
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-medium text-popover-foreground">
                {getUserDisplayName()}
              </p>
              {getUserEmail() && (
                <p className="text-xs text-muted-foreground">
                  {getUserEmail()}
                </p>
              )}
            </div>
            <button
              onClick={handleSettingsClick}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-popover-foreground hover:bg-muted transition-colors"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <hr className="border-border" />
            <button
              onClick={handleLogoutClick}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-destructive rounded-b-lg hover:bg-destructive-background transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        )}

        {/* Page Content - loading overlay only over content, sidebar stays visible */}
        <div className="flex-1 overflow-y-auto bg-background relative">
          {pageLoading && (
            <div className="min-h-screen bg-background flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading...</p>
              </div>
            </div>
          )}
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
